import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  CardActions,
  Button,
  IconButton,
} from "@mui/material";
import AddToCartButton from "../common/AddToCartButton";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { formatCurrency } from "../../utils/formatters";

/**
 * Product Card component
 * Displays a product in a card format
 *
 * @param {Object} props - Component props
 * @param {Object} props.product - Product data
 * @returns {JSX.Element} The ProductCard component
 */
const ProductCard = ({ product }) => {
  const { t } = useTranslation("common");
  const { t: tProducts } = useTranslation("products");
  const navigate = useNavigate();

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  const images = product.images || [];

  useEffect(() => {
    setCurrentImageIndex(0);
    setFadeIn(true);
  }, [product.id]);
  const hasMultipleImages = images.length > 1;

  const getCurrentImageUrl = () => {
    if (images.length > 0 && images[currentImageIndex]) {
      return images[currentImageIndex].imageUrl;
    }
    return product.imageUrl || "/placeholder.svg";
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setFadeIn(false);
    setTimeout(() => {
      setCurrentImageIndex((prev) =>
        prev === 0 ? images.length - 1 : prev - 1
      );
      setFadeIn(true);
    }, 150);
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setFadeIn(false);
    setTimeout(() => {
      setCurrentImageIndex((prev) =>
        prev === images.length - 1 ? 0 : prev + 1
      );
      setFadeIn(true);
    }, 150);
  };

  const handleProductClick = () => {
    navigate(`/products/${product.id}`);
  };

  // Check if product is variable
  const isVariableProduct = product.productType === "variable";

  // Log product data for debugging
  useEffect(() => {
    // Safety check - we should never be rendering a product with a parentId (variation)
    if (product.parentId) {
      console.error(
        `Warning: Rendering a variation product (${product.id}) with parent ${product.parentId}. This should not happen!`
      );
    }

    console.log(`Rendering ProductCard for: ${product.id}`, {
      name: product.name,
      productType: product.productType,
      isParent: !product.parentId,
      parentId: product.parentId || "none",
      price: isVariableProduct
        ? `Range: $${product.priceRange?.min}-$${product.priceRange?.max}`
        : `$${product.price}`,
      hasStock: product.hasStock,
      inStock: isInStock(),
      variationsCount: isVariableProduct
        ? product.variations?.length || 0
        : "N/A",
    });
  }, [product]);

  // Determine price to display
  const getPriceDisplay = () => {
    if (isVariableProduct) {
      // Use variation summary from optimized backend
      if (product.variationSummary) {
        const min = parseFloat(product.variationSummary.minPrice);
        const max = parseFloat(product.variationSummary.maxPrice);

        if (min === max) {
          return formatCurrency(min);
        } else if (min > 0 && max > 0) {
          return `${formatCurrency(min)} - ${formatCurrency(max)}`;
        }
      }

      // Legacy fallback for when we have full variations (rarely used now)
      if (product.variations && product.variations.length > 0) {
        const prices = product.variations
          .map((v) => parseFloat(v.price))
          .filter((p) => !isNaN(p) && p > 0);

        if (prices.length > 0) {
          const min = Math.min(...prices);
          const max = Math.max(...prices);

          if (min === max) {
            return formatCurrency(min);
          } else {
            return `${formatCurrency(min)} - ${formatCurrency(max)}`;
          }
        }
      }

      // Fallback to parent price if it exists
      if (product.price && parseFloat(product.price) > 0) {
        return formatCurrency(parseFloat(product.price));
      }

      // Last resort
      return tProducts("details.priceVariation");
    }

    // Simple product - show direct price
    return formatCurrency(parseFloat(product.price || 0));
  };

  // Determine if product is in stock
  const isInStock = () => {
    if (isVariableProduct) {
      // Use variation summary from optimized backend
      if (product.variationSummary) {
        return product.variationSummary.inStock;
      }

      // Legacy fallback for when we have full variations (rarely used now)
      if (product.variations && product.variations.length > 0) {
        return product.variations.some((v) => parseInt(v.stock) > 0);
      }

      // Fallback to parent stock (though this might not be accurate for variables)
      return parseInt(product.stock) > 0;
    }

    // Simple product - use direct stock
    return parseInt(product.stock) > 0;
  };

  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trimEnd() + "…";
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 3,
        },
      }}
    >
      <Box
        onClick={handleProductClick}
        sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
      >
        <Box
          sx={{
            position: "relative",
            height: 200,
            overflow: "hidden",
            "&:hover .image-nav-arrow": {
              opacity: 1,
            },
          }}
        >
          <CardMedia
            component="img"
            height="200"
            image={getCurrentImageUrl()}
            alt={product.name}
            sx={{
              objectFit: "cover",
              transition: "opacity 0.3s ease-in-out",
              opacity: fadeIn ? 1 : 0,
            }}
          />
          {hasMultipleImages && (
            <>
              <IconButton
                className="image-nav-arrow"
                size="small"
                onClick={handlePrevImage}
                aria-label={tProducts("card.previousImage")}
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  borderRadius: 0,
                  width: 36,
                  opacity: 0,
                  color: "rgba(255, 255, 255, 0.85)",
                  background: "linear-gradient(to right, rgba(0,0,0,0.15), transparent)",
                  transition: "opacity 0.2s ease",
                  "&:hover": {
                    color: "white",
                    background: "linear-gradient(to right, rgba(0,0,0,0.25), transparent)",
                  },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 28 }} />
              </IconButton>
              <IconButton
                className="image-nav-arrow"
                size="small"
                onClick={handleNextImage}
                aria-label={tProducts("card.nextImage")}
                sx={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  bottom: 0,
                  borderRadius: 0,
                  width: 36,
                  opacity: 0,
                  color: "rgba(255, 255, 255, 0.85)",
                  background: "linear-gradient(to left, rgba(0,0,0,0.15), transparent)",
                  transition: "opacity 0.2s ease",
                  "&:hover": {
                    color: "white",
                    background: "linear-gradient(to left, rgba(0,0,0,0.25), transparent)",
                  },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 28 }} />
              </IconButton>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: 0.5,
                }}
              >
                {images.map((image, index) => (
                  <Box
                    key={image.id ?? index}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor:
                        index === currentImageIndex
                          ? "white"
                          : "rgba(255, 255, 255, 0.5)",
                      transition: "background-color 0.3s",
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
        {isVariableProduct && (
          <Chip
            label={tProducts("shared.productType.variable")}
            color="primary"
            size="small"
            sx={{ position: "absolute", top: 8, right: 8 }}
          />
        )}
        {!isInStock() && (
          <Chip
            label={tProducts("shared.status.outOfStock")}
            color="error"
            size="small"
            sx={{ position: "absolute", top: 8, left: 8 }}
          />
        )}
        <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
          {product.description && (
            <Typography
              gutterBottom
              variant="h6"
              component="div"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                marginBottom: 2,
              }}
            >
              {truncateText(product.name, 26)}
            </Typography>
          )}
          {product.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                height: 40,
                mb: 1,
                flexGrow: 1,
              }}
            >
              {product.description}
            </Typography>
          )}
          {product.weight != null && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {tProducts("details.weight")} - {product.weight} ct
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginTop: "auto",
            }}
          >
            {!product.description && (
              <Typography
                variant="h6"
                component="div"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  marginBottom: 1,
                }}
              >
                {truncateText(product.name, 26)}
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
              }}
            >
              <Typography variant="h6" color="primary">
                {getPriceDisplay()}
              </Typography>
              {product.sku && (
                <Typography variant="caption" color="text.secondary">
                  {tProducts("details.sku", { sku: product.sku })}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Box>
      <CardActions sx={{ p: 2, pt: 0 }}>
        {isVariableProduct ? (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<ShoppingBagIcon />}
            onClick={handleProductClick}
            disabled={!isInStock()}
          >
            {tProducts("card.selectOptions")}
          </Button>
        ) : (
          <AddToCartButton
            productId={product.id}
            stock={product.stock}
            minOrderQuantity={1}
            fullWidth={true}
            showQuantity={false}
          />
        )}
      </CardActions>
    </Card>
  );
};

export default ProductCard;
